import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, race } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';

export interface NewsletterPost {
  id: number | string;
  name: string;
  description: string;
  excerpt: string;
  publishDate: Date;
  link: string;
  imageUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class SubstackService {
  private readonly DEFAULT_IMAGE = '/assets/img/newsletter-default.jpg';

  private cache: NewsletterPost[] | null = null;
  private cacheTime: number = 0;
  private readonly CACHE_DURATION = 300000; // 5 minutos

  constructor(private http: HttpClient) { }

  getPosts(): Observable<NewsletterPost[]> {
    // Si hay caché válido, lo retornamos inmediatamente
    if (this.cache && (Date.now() - this.cacheTime) < this.CACHE_DURATION) {
      console.log('✅ Usando caché del newsletter');
      return of(this.cache);
    }

    console.log('⏳ Cargando newsletter...');
    const startTime = Date.now();

    // Intentamos con rss2json PRIMERO (sin count parameter)
    return this.fetchWithRss2Json().pipe(
      catchError(() => {
        console.warn('⚠️ rss2json falló, intentando allorigins...');
        return this.fetchWithAllOrigins();
      }),
      catchError(() => {
        console.warn('⚠️ allorigins falló, intentando codetabs...');
        return this.fetchWithCodeTabs();
      }),
      map(posts => {
        this.cache = posts;
        this.cacheTime = Date.now();
        console.log(`✅ Newsletter cargado en ${Date.now() - startTime}ms - ${posts.length} posts`);
        return posts;
      }),
      catchError((error) => {
        console.error('❌ Todos los métodos fallaron:', error);
        return of(this.getFallbackPosts());
      })
    );
  }

  private fetchWithRss2Json(): Observable<NewsletterPost[]> {
    const url = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent('https://lightcapgroupok.substack.com/feed')}`;

    return this.http.get<any>(url).pipe(
      timeout(5000), // 5 segundos máximo
      map(response => {
        if (response.status !== 'ok' || !response.items) {
          throw new Error('rss2json respuesta inválida');
        }
        return this.transformPosts(response.items.slice(0, 6));
      })
    );
  }

  private fetchWithAllOrigins(): Observable<NewsletterPost[]> {
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent('https://lightcapgroupok.substack.com/feed')}`;

    return this.http.get(proxyUrl, { responseType: 'text' }).pipe(
      timeout(5000),
      map(xmlString => this.parseXmlToPosts(xmlString))
    );
  }

  private fetchWithCodeTabs(): Observable<NewsletterPost[]> {
    const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent('https://lightcapgroupok.substack.com/feed')}`;

    return this.http.get(proxyUrl, { responseType: 'text' }).pipe(
      timeout(5000),
      map(xmlString => this.parseXmlToPosts(xmlString))
    );
  }

  private parseXmlToPosts(xmlString: string): NewsletterPost[] {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
    const items = xmlDoc.querySelectorAll('item');

    return Array.from(items).slice(0, 6).map((item, index) => {
      const title = item.querySelector('title')?.textContent || `Publicación ${index + 1}`;
      const description = item.querySelector('description')?.textContent || '';
      const link = item.querySelector('link')?.textContent || '';
      const pubDate = item.querySelector('pubDate')?.textContent || '';
      const content = item.querySelector('content\\:encoded')?.textContent || description;

      return {
        id: index + 1,
        name: this.cleanText(title),
        description: this.extractShortDescription(description),
        excerpt: '',
        publishDate: new Date(pubDate),
        link: link,
        imageUrl: this.extractImageUrlFromHtml(content) || this.DEFAULT_IMAGE
      };
    });
  }

  private transformPosts(items: any[]): NewsletterPost[] {
    return items.map((item: any, index: number) => ({
      id: item.guid || index + 1,
      name: this.cleanText(item.title || `Publicación ${index + 1}`),
      description: this.extractShortDescription(item.description || ''),
      excerpt: '',
      publishDate: new Date(item.pubDate || item.published),
      link: item.link || '',
      imageUrl: this.extractImageUrl(item)
    }));
  }

  private cleanText(text: string): string {
    return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private extractShortDescription(html: string): string {
    if (!html) return '';
    const clean = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return clean.length > 150 ? clean.substring(0, 150) + '...' : clean;
  }

  private extractImageUrl(item: any): string {
    if (item.enclosure?.link) return item.enclosure.link;
    if (item.thumbnail) return item.thumbnail;
    if (item.content) {
      const imgMatch = item.content.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch && imgMatch[1]) return imgMatch[1];
    }
    return this.DEFAULT_IMAGE;
  }

  private extractImageUrlFromHtml(html: string): string | null {
    const imgMatch = html.match(/<img[^>]+src="([^">]+)"/);
    return imgMatch && imgMatch[1] ? imgMatch[1] : null;
  }

  private getFallbackPosts(): NewsletterPost[] {
    return [
      {
        id: 1,
        name: 'Una nueva regulación establece cómo deberán invertirse los recursos del FAL',
        description: 'La obligatoriedad se acerca y anticiparse puede hacer la diferencia',
        excerpt: '',
        publishDate: new Date(),
        link: 'https://lightcapgroupok.substack.com',
        imageUrl: this.DEFAULT_IMAGE
      },
      {
        id: 2,
        name: 'La deuda que muchas PyMEs ignoran, hasta que el financiamiento se corta',
        description: 'El historial crediticio de una empresa no se construye únicamente con préstamos',
        excerpt: '',
        publishDate: new Date(),
        link: 'https://lightcapgroupok.substack.com',
        imageUrl: this.DEFAULT_IMAGE
      },
      {
        id: 3,
        name: 'Cheques propios en el Mercado de Capitales',
        description: 'Una herramienta de financiamiento que muchas PyMEs todavía desconocen',
        excerpt: '',
        publishDate: new Date(),
        link: 'https://lightcapgroupok.substack.com',
        imageUrl: this.DEFAULT_IMAGE
      }
    ];
  }
}